import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Link as LinkIcon, 
  Clock, 
  Check, 
  X, 
  Eye, 
  Trash,
  Warning,
  Phone,
  Envelope,
  Calendar,
  ChatCircle,
  File,
  CaretDown,
  CaretUp,
  MagnifyingGlass,
  Funnel
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DocumentViewer } from '../components/DocumentViewer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TemporaryLinksPage = () => {
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLink, setSelectedLink] = useState(null);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewDocs, setReviewDocs] = useState([]);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [expandedLinks, setExpandedLinks] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/temporary-links`);
      setLinks(data);
    } catch (e) {
      toast.error('Failed to fetch links');
    } finally {
      setLoading(false);
    }
  };

  const handleExpire = async (linkId) => {
    if (!window.confirm('Expire this link?')) return;
    try {
      await axios.post(`${API_URL}/api/temporary-links/${linkId}/expire`);
      toast.success('Link expired');
      fetchLinks();
    } catch (e) {
      toast.error('Failed to expire link');
    }
  };

  const handleApprove = async (linkId) => {
    try {
      await axios.post(`${API_URL}/api/temporary-links/${linkId}/approve`);
      toast.success('Submission approved - changes applied to record');
      setIsReviewDialogOpen(false);
      setSelectedLink(null);
      fetchLinks();
    } catch (e) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async (linkId) => {
    try {
      await axios.post(`${API_URL}/api/temporary-links/${linkId}/reject`, { reason: rejectionReason });
      toast.success('Submission rejected');
      setIsReviewDialogOpen(false);
      setSelectedLink(null);
      setRejectionReason('');
      fetchLinks();
    } catch (e) {
      toast.error('Failed to reject');
    }
  };

  const openReviewDialog = async (link) => {
    setSelectedLink(link);
    setIsReviewDialogOpen(true);
    // Fetch actual documents for the record
    try {
      const { data } = await axios.get(`${API_URL}/api/documents/${link.record_id}`);
      setReviewDocs(data);
    } catch (e) {
      setReviewDocs([]);
    }
  };

  const toggleExpand = (linkId) => {
    setExpandedLinks(prev => ({
      ...prev,
      [linkId]: !prev[linkId]
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return <Badge variant="default">Active</Badge>;
      case 'submitted': return <Badge className="bg-amber-500">Pending Approval</Badge>;
      case 'approved': return <Badge className="bg-green-600">Approved</Badge>;
      case 'rejected': return <Badge variant="destructive">Rejected</Badge>;
      case 'expired': return <Badge variant="outline">Expired</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getInteractionIcon = (type) => {
    switch (type) {
      case 'call': return <Phone size={14} />;
      case 'email': return <Envelope size={14} />;
      case 'meeting': return <Calendar size={14} />;
      default: return <ChatCircle size={14} />;
    }
  };

  const [copyDialogUrl, setCopyDialogUrl] = useState('');

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied to clipboard');
    } catch {
      // Clipboard API blocked (iframe/permissions) — show dialog
      setCopyDialogUrl(text);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton"></div>
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="h-32 skeleton rounded-md"></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold">Temporary Links</h1>
        <p className="text-muted-foreground">Manage cross-branch access links</p>
      </div>

      {/* Search & Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Search</Label>
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by token, creator, branch, notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-temp-links"
                />
              </div>
            </div>
            <div className="w-48">
              <Label className="text-xs text-muted-foreground mb-1 block">Status</Label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                data-testid="filter-link-status"
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="submitted">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {(() => {
        const term = searchTerm.toLowerCase();
        const filteredLinks = links.filter(link => {
          if (statusFilter && link.status !== statusFilter) return false;
          if (!term) return true;
          return (
            (link.token || '').toLowerCase().includes(term) ||
            (link.created_by_name || '').toLowerCase().includes(term) ||
            (link.submitted_by_name || '').toLowerCase().includes(term) ||
            (link.target_branch_name || '').toLowerCase().includes(term) ||
            (link.source_branch_name || '').toLowerCase().includes(term) ||
            (link.status || '').toLowerCase().includes(term) ||
            (link.submission_data?.notes || '').toLowerCase().includes(term) ||
            (link.link_url || '').toLowerCase().includes(term)
          );
        });

        return filteredLinks.length > 0 ? (
        <div className="space-y-4">
          {filteredLinks.map(link => (
            <Card key={link.id} className="card-hover">
              <CardContent className="p-6">
                <div className="flex flex-col gap-4">
                  {/* Header row */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <LinkIcon size={20} className="text-primary" />
                        <span className="font-mono text-sm">
                          {link.token?.slice(0, 16)}...
                        </span>
                        {getStatusBadge(link.status)}
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>Created by: {link.created_by_name}</p>
                        <p>
                          Shared with: <span className="font-medium text-foreground">
                            {link.is_external ? 'External (Policy Auth)' : (link.target_branch_name || '-')}
                          </span>
                        </p>
                        <p>Expires: {new Date(link.expires_at).toLocaleString()}</p>
                        {link.submitted_by_name && (
                          <p className="text-primary font-medium">Submitted by: {link.submitted_by_name}</p>
                        )}
                        {link.allowed_ips?.length > 0 && (
                          <p>Allowed IPs: {link.allowed_ips.join(', ')}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/records/${link.record_id}`}>
                        <Button variant="outline" size="sm" data-testid={`view-record-${link.id}`}>
                          <Eye size={16} className="mr-1" /> View Record
                        </Button>
                      </Link>
                      
                      {link.status === 'active' && link.link_url && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => copyToClipboard(link.link_url)}
                          data-testid={`copy-link-${link.id}`}
                        >
                          <LinkIcon size={16} className="mr-1" /> Copy Link
                        </Button>
                      )}
                      
                      {/* Show Review button for submitted links */}
                      {link.status === 'submitted' && (link.created_by === user?.id || user?.role === 'super_admin' || user?.role === 'branch_admin') && (
                        <Button 
                          size="sm" 
                          onClick={() => openReviewDialog(link)}
                          className="bg-amber-500 hover:bg-amber-600"
                          data-testid={`review-link-${link.id}`}
                        >
                          <Eye size={16} className="mr-1" /> Review Submission
                        </Button>
                      )}
                      
                      {link.status === 'active' && (link.created_by === user?.id || user?.role === 'super_admin') && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleExpire(link.id)}
                          data-testid={`expire-link-${link.id}`}
                        >
                          <Trash size={16} className="mr-1" /> Expire
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Submission preview for submitted links */}
                  {link.status === 'submitted' && link.submission_data && (
                    <div className="border-t pt-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpand(link.id)}
                        className="mb-2"
                      >
                        {expandedLinks[link.id] ? <CaretUp size={16} className="mr-1" /> : <CaretDown size={16} className="mr-1" />}
                        {expandedLinks[link.id] ? 'Hide' : 'Preview'} Submission Details
                      </Button>
                      
                      {expandedLinks[link.id] && (
                        <div className="space-y-3 bg-amber-50 p-4 rounded-md">
                          {/* Interactions */}
                          {link.submission_data.interactions?.length > 0 && (
                            <div>
                              <Label className="text-xs text-muted-foreground">New Interactions ({link.submission_data.interactions.length})</Label>
                              <div className="space-y-2 mt-2">
                                {link.submission_data.interactions.map((interaction, idx) => (
                                  <div key={idx} className="flex items-start gap-2 p-2 bg-white rounded">
                                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                                      {getInteractionIcon(interaction.type)}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-xs capitalize">{interaction.type}</Badge>
                                        {interaction.status_change && (
                                          <Badge variant="secondary" className="text-xs">
                                            → {interaction.status_change}
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-sm mt-1">{interaction.notes}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Documents */}
                          {link.submission_data.documents?.length > 0 && (
                            <div>
                              <Label className="text-xs text-muted-foreground">Uploaded Documents ({link.submission_data.documents.length})</Label>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {link.submission_data.documents.map((docId, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    <File size={12} className="mr-1" /> Document #{idx + 1}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Notes */}
                          {link.submission_data.notes && (
                            <div>
                              <Label className="text-xs text-muted-foreground">Additional Notes</Label>
                              <p className="text-sm mt-1 bg-white p-2 rounded">{link.submission_data.notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="empty-state py-12">
            <LinkIcon size={48} className="mb-4 opacity-50" />
            <p className="text-lg font-medium">No temporary links found</p>
            <p className="text-sm">{searchTerm || statusFilter ? 'Try adjusting your search or filters' : 'Create temporary links from record details'}</p>
          </CardContent>
        </Card>
      );
      })()}

      {/* Review Submission Dialog */}
      <Dialog open={isReviewDialogOpen} onOpenChange={setIsReviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Submission</DialogTitle>
            <DialogDescription>
              Review the changes submitted by {selectedLink?.submitted_by_name} before approving or rejecting
            </DialogDescription>
          </DialogHeader>
          
          {selectedLink?.submission_data && (
            <div className="space-y-4">
              {/* Submitted At */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock size={14} />
                Submitted: {new Date(selectedLink.submitted_at).toLocaleString()}
              </div>

              {/* Interactions */}
              {selectedLink.submission_data.interactions?.length > 0 && (
                <div>
                  <Label className="font-medium">Interactions to Add ({selectedLink.submission_data.interactions.length})</Label>
                  <div className="space-y-2 mt-2">
                    {selectedLink.submission_data.interactions.map((interaction, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-secondary/50 rounded-md">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          {getInteractionIcon(interaction.type)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs capitalize">{interaction.type}</Badge>
                            {interaction.status_change && (
                              <Badge className="bg-amber-500 text-xs">
                                Status → {interaction.status_change}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm mt-1">{interaction.notes}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents */}
              {reviewDocs.length > 0 && (
                <div>
                  <Label className="font-medium">Documents ({reviewDocs.length})</Label>
                  <div className="space-y-2 mt-2">
                    {reviewDocs.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-secondary/50 rounded-md">
                        <div className="flex items-center gap-2 min-w-0">
                          <File size={16} className="text-muted-foreground shrink-0" />
                          <span className="text-sm truncate">{doc.original_filename || doc.filename}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setViewingDoc(doc)}>
                          View
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedLink.submission_data.notes && (
                <div>
                  <Label className="font-medium">Additional Notes</Label>
                  <div className="p-3 bg-secondary/50 rounded-md mt-2">
                    <p className="text-sm">{selectedLink.submission_data.notes}</p>
                  </div>
                </div>
              )}

              {/* No changes */}
              {!selectedLink.submission_data.interactions?.length && 
               !selectedLink.submission_data.documents?.length && 
               !selectedLink.submission_data.notes && (
                <div className="text-center py-4 text-muted-foreground">
                  <Warning size={24} className="mx-auto mb-2" />
                  <p>No changes were submitted</p>
                </div>
              )}

              {/* Rejection Reason */}
              <div className="border-t pt-4">
                <Label>Rejection Reason (optional)</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Provide a reason if rejecting..."
                  className="mt-2"
                  data-testid="rejection-reason"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsReviewDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => handleReject(selectedLink.id)}
                  data-testid="reject-submission-btn"
                >
                  <X size={16} className="mr-1" /> Reject
                </Button>
                <Button 
                  onClick={() => handleApprove(selectedLink.id)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="approve-submission-btn"
                >
                  <Check size={16} className="mr-1" /> Approve & Apply Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DocumentViewer document={viewingDoc} open={!!viewingDoc} onClose={() => setViewingDoc(null)} />

      {/* Copy Link Dialog (fallback when clipboard API blocked) */}
      <Dialog open={!!copyDialogUrl} onOpenChange={() => setCopyDialogUrl('')}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Copy Link</DialogTitle>
            <DialogDescription>Select the URL below and copy it (Ctrl+C / Cmd+C)</DialogDescription>
          </DialogHeader>
          <input
            readOnly
            value={copyDialogUrl}
            onFocus={(e) => e.target.select()}
            className="w-full px-3 py-2 text-sm font-mono bg-secondary border rounded-md select-all"
            data-testid="copy-link-input"
          />
          <Button size="sm" variant="outline" onClick={() => setCopyDialogUrl('')}>Close</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemporaryLinksPage;
